import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PieChart, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_COLORS = ['#97AAB3', '#3B82F6', '#EAB308', '#F97316', '#EF4444', '#DC2626'];

function MiniDonut({ data, size = 100 }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  return (
    <svg width={size} height={size} className="-rotate-90">
      {data.filter(d => d.value > 0).map((d, i) => {
        const pct = d.value / total;
        const dash = circ * pct;
        const currentOffset = offset;
        offset += dash;
        return <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={d.color} strokeWidth={12}
          strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-currentOffset} />;
      })}
    </svg>
  );
}

export function ExecutiveDashboardPage() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, trendsRes, heatRes] = await Promise.all([
        axios.get(`${API}/dashboard/realtime`, { headers: getAuthHeader() }),
        axios.get(`${API}/dashboard/trends?days=${period}`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/heatmap?days=${period}`, { headers: getAuthHeader() }),
      ]);
      setData({ dash: dashRes.data, heat: heatRes.data });
      setTrends(trendsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeader, period]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  const dash = data?.dash || {};
  const heat = data?.heat || {};
  const sevDist = dash.severity_distribution || [];
  const catDist = dash.category_distribution || [];
  const incidentsTrend = trends?.incidents_trend || [];
  const mttrGlobal = trends?.mttr_global || 0;
  const totalResolved = trends?.total_resolved || 0;
  const hostsSummary = dash.hosts_summary || {};
  const incidents = dash.incidents || {};
  const triggersSummary = dash.triggers_summary || {};

  const donutData = sevDist.map((s, i) => ({ name: s.name, value: s.value, color: SEVERITY_COLORS[i] || '#6B7280' }));
  const catDonut = catDist.slice(0, 6).map((c, i) => ({ name: c.name, value: c.value, color: ['#3B82F6', '#EF4444', '#F97316', '#22C55E', '#8B5CF6', '#EC4899'][i] || '#6B7280' }));

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Dashboard Executivo</h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
            <SelectItem value="60">60 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Server className="h-4 w-4 text-muted-foreground" /><span className="text-xs text-muted-foreground uppercase">Hosts</span></div>
          <p className="text-2xl font-bold">{hostsSummary.total || 0}</p>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-red-500" /><span className="text-xs text-muted-foreground uppercase">Alertas</span></div>
          <p className="text-2xl font-bold text-red-600">{incidents.active || 0}</p>
        </CardContent></Card>
        <Card className="border-green-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-4 w-4 text-green-500" /><span className="text-xs text-muted-foreground uppercase">Resolvidos</span></div>
          <p className="text-2xl font-bold text-green-600">{totalResolved || incidents.resolved || 0}</p>
        </CardContent></Card>
        <Card className="border-blue-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-blue-500" /><span className="text-xs text-muted-foreground uppercase">MTTR</span></div>
          <p className="text-2xl font-bold text-blue-600">{mttrGlobal}min</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Activity className="h-4 w-4 text-muted-foreground" /><span className="text-xs text-muted-foreground uppercase">Incidentes</span></div>
          <p className="text-2xl font-bold">{heat.total_events || 0}</p>
          <p className="text-xs text-muted-foreground">no período</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Severity Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider">Severidade</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-6">
            <MiniDonut data={donutData} size={110} />
            <div className="space-y-1.5 flex-1">
              {sevDist.filter(s => s.value > 0).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || SEVERITY_COLORS[i] }} />
                    <span>{s.name}</span>
                  </div>
                  <span className="font-bold">{s.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Category Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider">Categorias</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-6">
            <MiniDonut data={catDonut} size={110} />
            <div className="space-y-1.5 flex-1">
              {catDist.slice(0, 6).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="truncate mr-2">{c.name}</span>
                  <span className="font-bold shrink-0">{c.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommendations from heatmap */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider">Pontos de Atenção</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(heat.recommendations || []).map((r, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                  <span>{r}</span>
                </div>
              ))}
              {(!heat.recommendations || heat.recommendations.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">Sem alertas</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Line */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />Tendência de Incidentes ({period}d)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {incidentsTrend.length > 0 ? (() => {
            const vals = incidentsTrend.map(d => d.value);
            const max = Math.max(...vals, 1);
            return (
              <div className="flex items-end justify-between gap-0.5 h-32">
                {incidentsTrend.map((d, i) => {
                  const pct = (d.value / max) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                      {i % Math.ceil(incidentsTrend.length / 10) === 0 && <span className="text-xs font-bold">{d.value}</span>}
                      <div className="w-full rounded-t" style={{ height: `${Math.max(pct, d.value > 0 ? 3 : 0)}%`, backgroundColor: i === incidentsTrend.length - 1 ? '#F97316' : '#3B82F6' }} />
                      {i % Math.ceil(incidentsTrend.length / 7) === 0 && <span className="text-xs text-muted-foreground">{d.label}</span>}
                    </div>
                  );
                })}
              </div>
            );
          })() : <p className="text-sm text-muted-foreground text-center py-8">Sem dados</p>}
        </CardContent>
      </Card>
    </div>
  );
}
